/**
 * Profile Stats Tab — score, streak, active days, heatmap, achievements.
 */

import React, { useState } from 'react'
import { Flame, Zap, Calendar, Clock, Trophy, Swords, Star } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useUserStats, useUserAchievements } from '@turnifly/shared/hooks'
import { usePomodoroStats } from '@turnifly/shared/hooks'
import { useChallengeStats } from '@turnifly/shared/hooks'
import { ActivityHeatmap } from './activity-heatmap'
import { AchievementList } from './achievement-list'
import { ProfileGoalsSection } from './profile-goals-section'
import { Skeleton } from '@turnifly/shared/ui'

interface ProfileStatsTabProps {
  userId: string
}

export const ProfileStatsTab: React.FC<ProfileStatsTabProps> = ({ userId }) => {
  const { t } = useTranslation()
  const { data: stats, isLoading: isStatsLoading } = useUserStats(userId)
  const currentYear = new Date().getFullYear()
  const [heatmapYear] = useState(currentYear)
  const { data: achievements, isLoading: isAchievementsLoading } = useUserAchievements(userId, currentYear)
  const { stats: pomodoroStats } = usePomodoroStats()
  const { data: challengeStats } = useChallengeStats(userId)

  return (
    <div className="space-y-4 mt-2">
      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 gap-2"
      >
        {isStatsLoading ? (
          <>
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
          </>
        ) : (
          <>
            <StatsCard
              icon={<Zap size={16} className="text-primary" />}
              value={stats?.totalScore ?? 0}
              label={t('profile.score')}
              color="primary"
            />
            <StatsCard
              icon={<Flame size={16} className="text-streak" />}
              value={stats?.currentStreak ?? 0}
              label={t('profile.streakLabel')}
              suffix={t('profile.days')}
              color="streak"
            />
            <StatsCard
              icon={<Calendar size={16} className="text-success" />}
              value={stats?.activeDays ?? 0}
              label={t('profile.activeDays')}
              color="success"
            />
            <StatsCard
              icon={<Clock size={16} className="text-red-400" />}
              value={pomodoroStats?.total_focus_minutes ?? 0}
              label={t('profile.focusTime')}
              formatFn={fmtFocus}
              color="focus"
            />
          </>
        )}
      </motion.div>

      {/* Activity Heatmap */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <h3 className="text-sm font-semibold text-text mb-3">
            {t('profile.activityHeatmap')}
          </h3>
          <ActivityHeatmap userId={userId} year={heatmapYear} />
        </div>
      </motion.div>

      {/* Achievements */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <h3 className="text-sm font-semibold text-text mb-3">
            {t('profile.achievements')} {currentYear}
          </h3>
          {isAchievementsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
          ) : (
            <AchievementList achievements={achievements ?? []} />
          )}
        </div>
      </motion.div>

      {/* Goals */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <h3 className="text-sm font-semibold text-text mb-3">
            {t('profile.goalsLabel')}
          </h3>
          <ProfileGoalsSection userId={userId} />
        </div>
      </motion.div>

      {/* Challenge Stats */}
      {challengeStats && (challengeStats.participating > 0 || challengeStats.completed > 0 || challengeStats.created > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
            <h3 className="text-sm font-semibold text-text mb-3">
              {t('profile.challengeStats')}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col items-center py-2.5 px-1 rounded-xl bg-white/[0.03]">
                <Swords size={14} className="text-primary mb-1" />
                <span className="text-base font-bold text-text">{challengeStats.participating}</span>
                <span className="text-[9px] text-text-hint text-center">{t('profile.participating')}</span>
              </div>
              <div className="flex flex-col items-center py-2.5 px-1 rounded-xl bg-white/[0.03]">
                <Trophy size={14} className="text-success mb-1" />
                <span className="text-base font-bold text-text">{challengeStats.completed}</span>
                <span className="text-[9px] text-text-hint text-center">{t('profile.completedChallenges')}</span>
              </div>
              <div className="flex flex-col items-center py-2.5 px-1 rounded-xl bg-white/[0.03]">
                <Star size={14} className="text-accent mb-1" />
                <span className="text-base font-bold text-text">{challengeStats.created}</span>
                <span className="text-[9px] text-text-hint text-center">{t('profile.createdChallenges')}</span>
              </div>
            </div>
            {challengeStats.completed > 0 && (
              <div className="mt-2 flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-success/[0.04] border border-success/[0.08]">
                <span className="text-xs text-success font-medium">
                  {t('profile.avgCompletion')}: {challengeStats.avgCompletionRate}%
                </span>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  )
}

// ── Stats Card ──────────────────────────────────────────────

function fmtFocus(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}s ${m}m` : `${h}s`
}

interface StatsCardProps {
  icon: React.ReactNode
  value: number
  label: string
  suffix?: string
  formatFn?: (v: number) => string
  color: 'primary' | 'streak' | 'success' | 'focus'
}

const StatsCard: React.FC<StatsCardProps> = React.memo(({ icon, value, label, suffix, formatFn, color }) => {
  const glowMap: Record<string, string> = {
    primary: 'shadow-[0_0_12px_rgba(0,97,255,0.1)]',
    streak: 'shadow-[0_0_12px_rgba(249,115,22,0.1)]',
    success: 'shadow-[0_0_12px_rgba(34,197,94,0.1)]',
    focus: 'shadow-[0_0_12px_rgba(239,68,68,0.1)]',
  }

  const display = formatFn ? formatFn(value) : value.toLocaleString()

  return (
    <div className={`
      flex flex-col items-center justify-center py-3 px-2 rounded-2xl
      bg-white/[0.03] border border-white/[0.06]
      ${glowMap[color]}
    `}>
      <div className="mb-1.5">{icon}</div>
      <span className="text-lg font-display font-bold text-text">
        {display}
      </span>
      <span className="text-[10px] text-text-hint leading-tight text-center">
        {label}{suffix ? ` ${suffix}` : ''}
      </span>
    </div>
  )
})
StatsCard.displayName = 'StatsCard'
